import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { CdkTextareaAutosize } from '@angular/cdk/text-field';
import {
  AfterViewInit,
  Component,
  EventEmitter,
  Inject,
  Input,
  Output,
  QueryList,
  signal,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { firstValueFrom } from 'rxjs';
import { DialogPromptAiComponent } from 'src/app/components/dialog-prompt-ai/dialog-prompt-ai.component';
import { SpinnerOverlayComponent } from 'src/app/components/spinner-overlay/spinner-overlay.component';
import {
  Domande,
  InformativaAziendaleService,
} from 'src/app/services/informativa-aziendale/informativa-aziendale.service';
import {
  CategorieUm,
  UnitaDiMisura,
  UnitaDiMisuraService,
} from 'src/app/services/unita-di-misura/unita-di-misura.service';
import { TipoElemento, UtilityService } from 'src/app/services/utility/utility.service';

@Component({
  selector: 'app-dialog-crea-domande-informativa',
  standalone: false,

  templateUrl: './dialog-crea-domande-informativa.component.html',
  styleUrl: './dialog-crea-domande-informativa.component.scss',
})
export class DialogCreaDomandeInformativaComponent implements AfterViewInit {
  private _inputDomande: Domande | undefined;

  @Input()
  set inputDomande(_inputDomande: Domande | undefined) {
    this._inputDomande = _inputDomande;
  }
  get inputDomande() {
    return this._inputDomande;
  }

  @Output() inserimentoDoamanda: EventEmitter<string> = new EventEmitter();

  /* GETTER USATI PER TIPIZZARE L'HTML */
  get opzioniFormArray(): FormArray {
    return this.formDomanda.get('opzioni') as FormArray;
  }

  get opzioniControls(): FormGroup[] {
    return this.opzioniFormArray.controls as FormGroup[];
  }

  get lineeGuidaForm() {
    return this.formDomanda?.controls?.metadati?.controls?.lineaGuida?.controls ?? [];
  }

  get mapCateg() {
    return (
      this.mappaCategorieUm[this.formDomanda.get('idCategoriaUnitaDiMisura')?.value ?? ''] ?? []
    );
  }

  get idDomanda() {
    return this.formDomanda.get('id')?.value ?? this.data?.domanda?.id ?? '';
  }

  //Signal per controllare la chiususra della dialog
  closeDialog = signal<boolean>(true);

  public formDomanda = new FormGroup({
    /* Identificatori */
    id: new FormControl<string | undefined>(undefined, {
      nonNullable: true,
      validators: [],
    }),
    chiave: new FormControl<string | undefined>(undefined, {
      nonNullable: true,
      validators: [],
    }),

    /* Contenuto principale */
    domanda: new FormControl<string | undefined>(undefined, {
      nonNullable: true,
      validators: [Validators.required, UtilityService.blankValidator],
    }),
    tipo: new FormControl<string | undefined>(undefined, {
      nonNullable: true,
      validators: [Validators.required, UtilityService.blankValidator],
    }),

    /* Metadati */
    metadati: new FormGroup({
      lineaGuida: new FormArray([new FormControl('', { nonNullable: true })]),
      tipo: new FormArray([new FormControl('', { nonNullable: true })]),
    }),

    /* Opzioni e configurazioni */
    note: new FormControl<string | undefined>(undefined, {
      nonNullable: true,
      validators: [],
    }),
    obbligatorio: new FormControl<boolean>(false, {
      nonNullable: true,
      validators: [],
    }),
    sottodomanda: new FormControl<boolean>(false, {
      nonNullable: true,
      validators: [],
    }),
    idCategoriaUnitaDiMisura: new FormControl<string | undefined>(undefined, {
      nonNullable: true,
      validators: [],
    }),
    idUnitaDiRiferimento: new FormControl<string | undefined>(undefined, {
      nonNullable: true,
      validators: [],
    }),
    dataUltimaModifica: new FormControl<string | undefined>(undefined, {
      nonNullable: true,
      validators: [],
    }),

    moduloCompleto: new FormControl<boolean>(false, {
      nonNullable: true,
      validators: [],
    }),

    /* Array di opzioni */
    opzioni: new FormArray(
      [
        new FormGroup({
          chiave: new FormControl<string | undefined>(undefined),
          id: new FormControl<string | undefined>(undefined),
          valore: new FormControl<string | undefined>(undefined),
          moduloCompleto: new FormControl<boolean>(false),
        }),
      ],
      {},
    ),
  });

  @ViewChild(SpinnerOverlayComponent) spinnerOver!: SpinnerOverlayComponent;

  @ViewChildren('inputDesc', { read: CdkTextareaAutosize })
  queryListTextarea!: QueryList<CdkTextareaAutosize>;
  @ViewChildren('inputLineaGuida', { read: CdkTextareaAutosize })
  queryListTextareaLineaGuida!: QueryList<CdkTextareaAutosize>;

  @Input() public arrayCategorie: CategorieUm[] = [];
  @Input() public mappaCategorieUm: {
    [idCategoriaUnitaDiMisura: string]: UnitaDiMisura[];
  } = {};

  constructor(
    public dialogRef: MatDialogRef<DialogCreaDomandeInformativaComponent>,
    private readonly unitaDiMisura: UnitaDiMisuraService,
    public readonly informativaAziendale: InformativaAziendaleService,
    private readonly utilityService: UtilityService,
    private readonly dialog: MatDialog,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      domanda: Domande;
      dialogCreaNuovo: boolean;
    },
  ) {}

  ngAfterViewInit(): void {
    this.spinnerOver.show();
    setTimeout(() => {
      if (!this.arrayCategorie.length && Object.keys(this.mappaCategorieUm).length === 0) {
        this.unitaDiMisura.getCategoria().subscribe((categorie) => {
          this.arrayCategorie = categorie;
          this.arrayCategorie.forEach((cate) => {
            if (cate?.id) {
              this.mappaCategorieUm[cate.id] = cate.unitaDiMisura || [];
            }
          });
        });
      }

      const domanda: Domande = this.inputDomande ?? this.data.domanda;

      if (domanda) {
        this._initDomanda(domanda);
      }
    }, 0);
    this.spinnerOver.hide();
  }

  /**
   * Metodo che mi valorizza la form Domanda
   * @param domanda
   */
  private _initDomanda(domanda: Domande) {
    (Object.keys(domanda) as (keyof Domande)[]).forEach((key) => {
      switch (key) {
        case 'metadati': {
          this._initMetadatiDomanda(domanda);
          break;
        }

        case 'opzioni': {
          this._initOpzioniDomanda(domanda);
          break;
        }

        default: {
          this._initRestOfForm(domanda, key);
          break;
        }
      }
    });
  }

  /**
   * Inizializzazione e mappatura dei metadati per la domanda sulla form
   * @param domanda
   */
  private _initMetadatiDomanda(domanda: Domande) {
    const metadatiGroup = this.formDomanda.get('metadati') as FormGroup;
    const lineaArray = metadatiGroup?.get('lineaGuida') as FormArray;
    const tipoArray = metadatiGroup?.get('tipo') as FormArray;
    const linee = domanda.metadati?.lineaGuida?.length ? domanda.metadati.lineaGuida : [''];
    /* Il primo elemento sarà sempre vuoto perchè valorizzato nella form per default, quindi lo rimuovo */
    while (lineaArray.length > 0) {
      lineaArray.removeAt(0);
    }

    linee.forEach((linea) => lineaArray.push(new FormControl(linea)));

    /* -------------------- */
    const tipo = domanda.metadati?.tipo?.length ? domanda.metadati.tipo : [''];
    /* Il primo elemento sarà sempre vuoto perchè valorizzato nella form per default, quindi lo rimuovo */
    while (tipoArray?.length > 0) {
      tipoArray.removeAt(0);
    }

    tipo.forEach((tipo) => tipoArray?.push(new FormControl(tipo)));
  }

  /**
   * Inizializzazione e mappatura delle opzioni per la domanda sulla form
   * @param domanda
   */
  private _initOpzioniDomanda(domanda: Domande) {
    const opzioniArray = this.formDomanda.get('opzioni') as FormArray;
    const opzioni = domanda.opzioni ?? [];

    // Se esiste almeno un'opzione valida e la prima ha un valore,
    // rimuovo l'elemento vuoto creato di default nel FormArray.
    if (opzioni.length > 0 && (opzioni[0].valore || opzioni[0].id || opzioni[0].chiave)) {
      opzioniArray.clear();
    }

    // Aggiungo al FormArray solo le opzioni realmente valorizzate
    opzioni
      .filter((op) => op.chiave || op.id || op.valore)
      .forEach((op) => {
        opzioniArray.push(
          new FormGroup({
            chiave: new FormControl(op.chiave),
            id: new FormControl(op.id),
            valore: new FormControl(op.valore),
            moduloCompleto: new FormControl(op.moduloCompleto ?? false),
          }),
        );
      });

    // Se dopo la mappatura non ci sono opzioni,
    // aggiungo un'opzione vuota per permettere la compilazione.
    if (opzioniArray.length === 0) {
      opzioniArray.push(
        new FormGroup({
          chiave: new FormControl(),
          id: new FormControl(),
          valore: new FormControl(),
          moduloCompleto: new FormControl(false),
        }),
      );
    }
  }

  /**
   * Inizializzazione e mappatura dei form control rimanenti
   * @param domanda
   */
  private _initRestOfForm(domanda: Domande, key: keyof Domande) {
    const control = this.formDomanda.get(key);
    if (
      control &&
      (typeof domanda[key] === 'string' ||
        typeof domanda[key] === 'boolean' ||
        domanda[key] === undefined)
    ) {
      control.setValue(domanda[key]);
    }
  }

  // Metodo per aggiungere una nuova linea guida
  /*  addLineaGuida(domandaArr: any) {
     (domandaArr.controls.metadati.controls.lineaGuida as FormArray).push(new FormControl(''));
   }
  */
  cambiaTipologia(event: any, formDomanda: FormGroup) {
    if (event.value !== 'numero_intero' && event.value !== 'numero_decimale') {
      formDomanda.get('idUnitaDiRiferimento')?.setValue(undefined);
      formDomanda.get('idCategoriaUnitaDiMisura')?.setValue(undefined);
    }
  }

  /**
   * Metodo salvataggio chiamato da HTML
   */
  async salvaInHtml() {
    this.closeDialog.set(true);
    await this.salvaDomanda();
    if (this.closeDialog()) this.dialogRef.close();
  }

  async salvaDomanda(): Promise<boolean> {
    if (this.formDomanda.invalid) {
      this.markFormControlsAsTouched();
      return false;
    }

    this.spinnerOver.show();

    try {
      const domanda: Domande = this.formDomanda.getRawValue() as Domande;

      const fnService$ = this.idDomanda
        ? this.informativaAziendale.putDomanda(this.idDomanda, domanda)
        : this.informativaAziendale.postDomanda(domanda);

      const result = await firstValueFrom(fnService$);

      if (result && !this.idDomanda) {
        this._initDomanda(result);
      }

      this.utilityService.openSnackBar('Salvataggio effettuato ', '', {
        duration: 2000,
        panelClass: ['success-snackbar'],
      });

      return true;
    } catch (err) {
      this.utilityService.openDialog({
        titolo: 'Attenzione',
        descrizione: 'Errore nel salvataggio della domanda',
        bottoni: [{ nome_btn: 'Chiudi' }],
      });
      this.closeDialog.set(false);
      return false;
    } finally {
      this.spinnerOver.hide();
    }
  }

  /**
   * Imposta tutti i controlli del form come "touched".
   *
   * Utilizzato quando il form non è valido, per forzare la visualizzazione
   * degli errori di validazione nei campi.
   */
  private markFormControlsAsTouched(): void {
    Object.values(this.formDomanda.controls).forEach((control) => {
      control.markAsTouched();
    });
    this.closeDialog.set(false);
  }

  dropOpzioni(event: CdkDragDrop<any[]>, formArrayOpzioni: FormArray) {
    const currentGroup = formArrayOpzioni.at(event.previousIndex);
    formArrayOpzioni.removeAt(event.previousIndex, { emitEvent: false });
    formArrayOpzioni.insert(event.currentIndex, currentGroup, { emitEvent: false });
  }

  elimina(formArray: FormArray, index: number) {
    formArray.removeAt(index);
  }

  addOpzioni(formArray: FormArray) {
    const opzioni = new FormGroup({
      chiave: new FormControl<string | undefined>(undefined),
      id: new FormControl<string | undefined>(undefined),
      valore: new FormControl<string | undefined>(undefined),
      moduloCompleto: new FormControl<boolean>(false),
    });
    formArray.push(opzioni);
  }

  /**
   * Metodo che mi manda un evento al padre con l'inserimento della domanda in tempo reale, per poi settarla sulla form del padre
   */
  sincronizzaDomanda() {
    const domanda = this.formDomanda.get('domanda')?.getRawValue();
    this.inserimentoDoamanda.emit(domanda);
  }

  /**
   * Metodo chiamato da html per la dialog prompt ai
   * prima salvo l'oggetto e poi apri la dialog
   */
  async ctrOpenPromptAI() {
    this.closeDialog.set(false);

    if (!this.idDomanda) {
      await this.salvaDomanda();
    }
    if (this.idDomanda) {
      this.promptAI();
    }
  }

  /**
   * Dialog per inserimneto prompt
   */
  private promptAI() {
    const tipoElemento: TipoElemento = 'DOMANDAINFORMATIVA';
    const dialogPromptAi = this.dialog.open(DialogPromptAiComponent, {
      data: {
        tipoElemento,
        idElemento: this.idDomanda,
      },
      panelClass: ['dialog-container'],
      disableClose: false,
      width: '50%',
      maxHeight: '95%',
      autoFocus: false,
    });

    dialogPromptAi.afterClosed().subscribe((result) => {
      //
    });
  }

  /**
   * Aggiorna tutti i figli quando clicco sul padre
   */
  updateAllChild(checked: boolean, formDomanda: FormGroup) {
    const opzioni = formDomanda.get('opzioni') as FormArray;
    if (opzioni && opzioni.length > 0) {
      opzioni.controls.forEach((ctrl) => {
        ctrl.get('moduloCompleto')?.setValue(checked, { emitEvent: false });
      });
    }
  }

  /**
   * Chiamata ogni volta che un singolo figlio viene cliccato.
   * Ricalcola lo stato del padre.
   */
  checkPadreDopoCambioFiglio(formDomanda: FormGroup) {
    const opzioni = formDomanda.get('opzioni') as FormArray;
    const controlloPadre = formDomanda.get('moduloCompleto');

    if (!opzioni || opzioni.length === 0) {
      // Se non ci sono figli, non facciamo nulla:
      // il padre mantiene il valore che ha (es. quello dal backend salvato)
      return;
    }

    const tuttiChecked = opzioni.controls.every((ctrl) => !!ctrl.get('moduloCompleto')?.value);

    // Aggiorna il padre senza scatenare updateAllChild di nuovo
    controlloPadre?.setValue(tuttiChecked, { emitEvent: false });
  }
}
